import ParticipantVirtual from '@client-shared/models/participant-virtual.model.js'
import Participant from '@client-shared/models/participant.model.js'

// getUserId is a safety wrapper for old, inconsistent client APP usage
const getUserId = userId => {
  if (userId && userId._id) {
    return userId._id
  }

  return userId
}

// getUserIdOfParticipant is a safety wrapper for old, inconsistent API which sometimes returned projects with not populated participants.user objects
const getUserIdOfParticipant = participant => {
  if (participant.user && participant.user._id) {
    return participant.user._id
  }

  return participant.user
}

// Lookup maps per participants array, built on the first lookup. The stores replace the array
// whenever a participant changes (SET_PROJECT, UPDATE_PARTICIPANT, ADD_PARTICIPANT), so a
// changed participant list is a new key and a map never goes stale. Where ids are not unique the
// first participant wins, like the Array.find the lookups replace
const participantMapsByParticipants = new WeakMap()

const getParticipantMaps = participants => {
  let participantMaps = participantMapsByParticipants.get(participants)

  if (!participantMaps) {
    participantMaps = {
      byUserId: new Map(),
      byId: new Map(),
    }

    for (const participant of participants) {
      const userId = getUserIdOfParticipant(participant)

      if (!participantMaps.byUserId.has(userId)) {
        participantMaps.byUserId.set(userId, participant)
      }

      if (!participantMaps.byId.has(participant._id)) {
        participantMaps.byId.set(participant._id, participant)
      }
    }

    participantMapsByParticipants.set(participants, participantMaps)
  }

  return participantMaps
}

export default class Project {
  constructor ({ _id, title, created, lastRevisionUpload, lastRevisionUploadPlans, lastRevisionUploadDocuments, participantProjectAccessLogging, trashed, trashedDate, modified, timezone, usedStorage, backupDataSize, language, roles, phasetags, phasetagsDocuments, uploadAlert, tags, taskTypes, photoAlbums, locations, participants, computed, topCategoriesPlansEnabled, topCategoriesPlans, topCategoriesDocumentsEnabled, topCategoriesDocuments, isTwoFactorAuthRequired }) {
    this._id = _id
    this.title = title
    this.created = created
    this.modified = modified
    this.usedStorage = usedStorage
    this.backupDataSize = backupDataSize
    this.language = language
    this.roles = roles
    this.phasetags = phasetags
    this.phasetagsDocuments = phasetagsDocuments
    this.topCategoriesPlansEnabled = topCategoriesPlansEnabled || false
    this.topCategoriesPlans = topCategoriesPlans || []
    this.topCategoriesDocumentsEnabled = topCategoriesDocumentsEnabled || false
    this.topCategoriesDocuments = topCategoriesDocuments || []
    this.uploadAlert = uploadAlert
    this.tags = tags
    this.taskTypes = taskTypes || []
    this.photoAlbums = photoAlbums || []
    this.locations = locations
    this.participants = (participants || []).map(p => {
      // When there is no _id it's not a real participant. This can happen in the projects/new form for example
      if (!p._id) {
        return p
      }

      return Object.freeze(new Participant(p))
    })
    this.computed = computed || {}
    this.timezone = timezone
    this.lastRevisionUpload = lastRevisionUpload
    this.lastRevisionUploadPlans = lastRevisionUploadPlans
    this.lastRevisionUploadDocuments = lastRevisionUploadDocuments
    this.trashed = trashed
    this.trashedDate = trashedDate
    this.participantProjectAccessLogging = participantProjectAccessLogging
    this.isTwoFactorAuthRequired = isTwoFactorAuthRequired

    this.computed = {
      ...this.computed,

      participantsVirtual: this.roles.map(r => Object.freeze(new ParticipantVirtual({ roles: [ r ] }))),
      ownerParticipant: this.participants ? this.participants.find(p => p.permission === 'owner') : undefined,
      isLocationPropertiesEnabled: this.locations.location1.isEnabled || this.locations.location2.isEnabled || this.locations.location3.isEnabled || this.locations.location4.isEnabled,
    }
  }

  findParticipantByUserId (userId) {
    return getParticipantMaps(this.participants).byUserId.get(getUserId(userId))
  }

  findParticipantById (participantId) {
    return getParticipantMaps(this.participants).byId.get(participantId)
  }

  findParticipantByEmail (email) {
    return this.participants.find(participant => participant.user.email.toLowerCase() === email.toLowerCase())
  }

  findParticipantVirtualByRole (role) {
    const participantVirtual = this.computed.participantsVirtual.find(participant => participant.roles[0] === role)

    if (!participantVirtual) {
      return new ParticipantVirtual({ roles: [ role ] })
    }

    return participantVirtual
  }

  findParticipantByPlanDocAuthor (itemAuthor) {
    if (itemAuthor.userId) {
      return this.findParticipantByUserId(itemAuthor.userId)
    }

    return this.findParticipantVirtualByRole(itemAuthor.role)
  }

  findParticipantByPhotoAuthor (photoAuthor) {
    if (photoAuthor.userId) {
      return this.findParticipantByUserId(photoAuthor.userId)
    }

    return this.findParticipantVirtualByRole(photoAuthor.role)
  }
}
